import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { styled } from 'styletron-react';
import { Button } from 'baseui/button';
import { Spinner } from 'baseui/spinner';
import { toaster } from 'baseui/toast';
import { FormControl } from 'baseui/form-control';
import { Input } from 'baseui/input';
import { Select } from 'baseui/select';
import { Checkbox, LABEL_PLACEMENT } from 'baseui/checkbox';
import { Modal, ModalHeader, ModalBody, ModalFooter, ModalButton, ROLE } from 'baseui/modal';
import {
  ChevronRight,
  ChevronDown,
  Building2,
  Layers,
  Home,
  Plus,
  Search,
  DoorOpen,
  RefreshCw,
  Trash2,
  Bell,
  Pencil,
  X,
} from 'lucide-react';

import { useNavigate } from 'react-router-dom';
import { isUserAdmin } from '../../utils/auth';
import { useUserPermissions } from '../../hooks/useUserPermissions';
import { useFdCan } from '../../hooks/useFdCan';
import { ReadOnlyFence } from '../../components/ReadOnlyFence';
import { noBuildingsText, selectHint, selectPlaceholder, showHikConnect, storedStatusLabel, storedTypeLabel, withStoredOption } from './estatesAccess';
import { useTenantIds, useTenant } from '../../hooks/useTenant';
import { directApiInstance } from '../../api/httpClient';
import { useEstateGroups, useEstates } from '../../hooks/useEstates';
import { useBuildings, useBulkAssignBuilding } from '../../hooks/useBuildings';
import { S3Image } from '../../components/S3Image';
import { getImageUrl } from '../../utils/s3ImageUtils';
import type { Estate, EstateGroup, Building } from '../../api/generated';
import { useUserEstate } from '../../hooks/useUserEstate';
import type { UserEstate } from '../../api/userEstate';
import { MantineDataTable } from '../../components/MantineDataTable';
import { UserEstateStatusSelector } from '../../components/UserEstateStatusSelector';
import { UserEstateExpirationDate } from '../../components/UserEstateExpirationDate';
import type { MRT_ColumnDef } from 'mantine-react-table';
import { imageApiHeaders, imageApiUrl, uploadedImageKey } from '../../services/imageApi';

// ─── Type aliases ────────────────────────────────────────────────────────────
type SelectionType = 'group' | 'building' | 'estate' | null;

interface FlatEstate {
  id: number;
  name: string;
  status?: string;
  estate_type_id?: number | null;
  estate_group_id?: number | null;
  building_id?: number | null;
  hik_access_level_id?: string | null;
  show_in_app?: boolean;
  estate_group?: { id: number; name: string } | null;
}

interface TreeBuilding extends Building {
  hik_access_level_id?: string | null;
  description?: string | null;
  estate_group_id?: number | null;
}

// ─── Layout shells ───────────────────────────────────────────────────────────
const Shell = styled('div', {
  display: 'flex',
  height: '100%',
  overflow: 'hidden',
  backgroundColor: '#fff',
});

const LeftPanel = styled('div', {
  width: '280px',
  minWidth: '280px',
  borderRight: '1px solid #E5E7EB',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  backgroundColor: '#FAFAFA',
});

const LeftHeader = styled('div', {
  padding: '20px 16px 12px',
  borderBottom: '1px solid #E5E7EB',
  backgroundColor: '#fff',
});

const LeftTitle = styled('div', {
  fontSize: '18px',
  fontWeight: 700,
  color: '#111827',
  marginBottom: '10px',
});

const SearchBox = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  backgroundColor: '#F3F4F6',
  borderRadius: '8px',
  padding: '7px 10px',
});

const SearchInput = styled('input', {
  border: 'none',
  outline: 'none',
  background: 'transparent',
  fontSize: '13px',
  color: '#374151',
  flex: 1,
  '::placeholder': { color: '#9CA3AF' },
});

const TreeScroll = styled('div', {
  flex: 1,
  overflowY: 'auto',
  padding: '8px 0',
});

const LeftFooter = styled('div', {
  padding: '10px 12px',
  borderTop: '1px solid #E5E7EB',
  backgroundColor: '#fff',
});

// ─── Tree node styles ────────────────────────────────────────────────────────
const TreeRow = styled('div', ({ $selected, $depth }: { $selected: boolean; $depth: number }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  paddingLeft: `${12 + $depth * 16}px`,
  paddingRight: '12px',
  paddingTop: '6px',
  paddingBottom: '6px',
  cursor: 'pointer',
  borderRadius: '6px',
  margin: '1px 6px',
  backgroundColor: $selected ? '#F0F0F0' : 'transparent',
  ':hover': { backgroundColor: $selected ? '#E8E8E8' : '#F3F4F6' },
  transition: 'background 0.1s',
}));

const TreeLabel = styled('div', ({ $bold }: { $bold: boolean }) => ({
  fontSize: '13px',
  fontWeight: $bold ? 600 : 400,
  color: '#1F2937',
  flex: 1,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

const TreeBadge = styled('div', {
  fontSize: '11px',
  color: '#6B7280',
  backgroundColor: '#E5E7EB',
  borderRadius: '10px',
  padding: '1px 6px',
  flexShrink: 0,
});

const HighlightMark = styled('mark', {
  backgroundColor: '#FEF08A',
  color: 'inherit',
  borderRadius: '2px',
  padding: '0 1px',
});

const Chevron = styled('div', ({ $open }: { $open: boolean }) => ({
  width: '16px',
  height: '16px',
  flexShrink: 0,
  transform: $open ? 'rotate(0deg)' : 'rotate(-90deg)',
  transition: 'transform 0.15s',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

// ─── Right panel styles ──────────────────────────────────────────────────────
const RightPanel = styled('div', {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
});

const RightHeader = styled('div', {
  padding: '20px 28px 0',
  backgroundColor: '#fff',
  borderBottom: '1px solid #E5E7EB',
  paddingBottom: '0',
});

const Breadcrumb = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  fontSize: '12px',
  color: '#6B7280',
  marginBottom: '4px',
});

const Crumb = styled('span', ({ $clickable }: { $clickable: boolean }) => ({
  color: $clickable ? '#2563EB' : '#6B7280',
  cursor: $clickable ? 'pointer' : 'default',
  ':hover': $clickable ? { textDecoration: 'underline' } : {},
}));

const RightPanelTitle = styled('div', {
  fontSize: '20px',
  fontWeight: 700,
  color: '#111827',
  paddingBottom: '16px',
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
});

const RightScroll = styled('div', {
  flex: 1,
  overflowY: 'auto',
  padding: '24px 28px',
});

const FormCard = styled('div', {
  backgroundColor: '#fff',
  border: '1px solid #E5E7EB',
  borderRadius: '12px',
  padding: '20px',
  marginBottom: '20px',
});

const CardTitle = styled('div', {
  fontSize: '14px',
  fontWeight: 600,
  color: '#374151',
  marginBottom: '16px',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

const StickyFooter = styled('div', {
  borderTop: '1px solid #E5E7EB',
  padding: '14px 28px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  backgroundColor: '#fff',
  gap: '10px',
});

const FooterActions = styled('div', {
  display: 'flex',
  gap: '10px',
  alignItems: 'center',
});

// ─── Hik Access Level selector (shared) ──────────────────────────────────────
const AccessLevelRow = styled('div', ({ $selected }: { $selected: boolean }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  padding: '8px 12px',
  cursor: 'pointer',
  backgroundColor: $selected ? 'color-mix(in srgb, var(--tenant-primary) 8%, white)' : 'transparent',
  borderLeft: `3px solid ${$selected ? 'var(--tenant-primary)' : 'transparent'}`,
  transition: 'background 0.1s',
  ':hover': { backgroundColor: $selected ? 'color-mix(in srgb, var(--tenant-primary) 12%, white)' : '#F9FAFB' },
}));

const AccessLevelDot = styled('div', ({ $on }: { $on: boolean }) => ({
  width: '16px',
  height: '16px',
  borderRadius: '4px',
  border: `2px solid ${$on ? 'var(--tenant-primary)' : '#D1D5DB'}`,
  backgroundColor: $on ? 'var(--tenant-primary)' : '#fff',
  flexShrink: 0,
  transition: 'all 0.1s',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const EmptyState = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  color: '#9CA3AF',
  gap: '12px',
  padding: '40px',
  textAlign: 'center',
});

const HikBadge = styled('span', ({ $level }: { $level: 'estate' | 'building' | 'group' | 'default' }) => ({
  fontSize: '11px',
  padding: '1px 7px',
  borderRadius: '10px',
  backgroundColor:
    $level === 'estate' ? '#DBEAFE' :
    $level === 'building' ? '#D1FAE5' :
    $level === 'group' ? '#FEF3C7' : '#F3F4F6',
  color:
    $level === 'estate' ? '#1E40AF' :
    $level === 'building' ? '#065F46' :
    $level === 'group' ? '#92400E' : '#6B7280',
}));

// ─── Select overrides (z-index fix) ──────────────────────────────────────────
const SELECT_OVERRIDES = {
  Popover: { props: { overrides: { Body: { style: { zIndex: '999999 !important' } } } } },
  Dropdown: { style: { zIndex: '999999 !important' } },
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const TYPE_OPTS = [
  { id: 1, label: 'Apartment' },
  { id: 2, label: 'House' },
  { id: 3, label: 'Commercial' },
  { id: 4, label: 'Parking' },
  { id: 5, label: 'Storage' },
];

const STATUS_OPTS = [
  { id: 'planning', label: 'Planning' },
  { id: 'preconstruction', label: 'Preconstruction' },
  { id: 'underconstruction', label: 'Under Construction' },
  { id: 'paused', label: 'Paused' },
  { id: 'completed', label: 'Completed' },
  { id: 'delivered', label: 'Delivered' },
  { id: 'cancelled', label: 'Cancelled' },
];

// Lightweight estates fetch for tree building
const useAllEstatesFlat = () =>
  useQuery({
    queryKey: ['estates-flat-tree'],
    queryFn: async () => {
      const token = localStorage.getItem('token');
      const apiKey = (() => {
        try { return JSON.parse(sessionStorage.getItem('businessData') || '{}').api_key || ''; } catch { return ''; }
      })();
      const r = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/crud/estate?limit=1000&order_by=name&order_direction=ASC`,
        { headers: { Authorization: `Bearer ${token}`, 'X-Api-Key': apiKey } }
      );
      if (!r.ok) return [] as FlatEstate[];
      const json = await r.json();
      return (json?.data || []) as FlatEstate[];
    },
    staleTime: 30_000,
  });

// ─── Main component ───────────────────────────────────────────────────────────
export const EstatesExplorer: React.FC = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { currentTenantId } = useTenantIds();
  const { tenant } = useTenant();
  const hikEnabled = !!(tenant?.settings as any)?.hikConnect?.enabled;

  const [isAdmin, setIsAdmin] = useState(false);
  // SAM-1108: the estate group roles read users only (no expiration or status changes), and the estate group admin
  // edits its estates but never moves one to another estate group (the server answers 400).
  const { isEstateGroupAdmin, isEstateGroupFrontDesk } = useUserPermissions();
  const canEditResidency = useFdCan('users', 'edit');
  // SAM-1312 (U19, U25): the estate group front desk only views estates; Hik-Connect is internal data, not shown to the estate group roles.
  const canEditEstates = useFdCan('estates', 'edit');
  const showHik = showHikConnect(hikEnabled, { isEstateGroupAdmin, isEstateGroupFrontDesk });
  const canChangeResidencyStatus = useFdCan('users', 'approve');
  const [treeSearch, setTreeSearch] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [selection, setSelection] = useState<{ type: SelectionType; id: number | null }>({ type: null, id: null });
  const [hikSectionOpen, setHikSectionOpen] = useState(false);

  // Confirm modal
  const [confirmModal, setConfirmModal] = useState<{
    open: boolean;
    title: string;
    body: string;
    onConfirm: () => void;
    danger?: boolean;
  }>({ open: false, title: '', body: '', onConfirm: () => {} });

  const openConfirm = (title: string, body: string, onConfirm: () => void, danger = false) =>
    setConfirmModal({ open: true, title, body, onConfirm, danger });
  const closeConfirm = () => setConfirmModal(p => ({ ...p, open: false }));

  // Hik levels
  const [hikLevels, setHikLevels] = useState<any[]>([]);
  const [hikLoading, setHikLoading] = useState(false);
  const [hikLoaded, setHikLoaded] = useState(false);

  // Data
  const { allEstateGroups, createEstateGroup, updateEstateGroup, patchEstateGroup, deleteEstateGroup, isCreating: cGroup, isUpdating: uGroup } = useEstateGroups();
  const { allBuildings, createBuilding, patchBuilding, deleteBuilding, isCreating: cBuilding, isUpdating: uBuilding } = useBuildings();
  const { createEstate, updateEstate, deleteEstate, isCreating: cEstate, isUpdating: uEstate } = useEstates({});
  const { data: allEstates = [], isFetching: estatesLoading } = useAllEstatesFlat();
  const bulkAssign = useBulkAssignBuilding();

  // ── Tree: group → buildings (via building.estate_group_id) → estates ────────
  // Structure: groupId → { buildingId → FlatEstate[] }
  // Buildings with estate_group_id appear under their group even with 0 estates.
  // Estates without building_id appear under null key (Unassigned).
  const treeData = useMemo(() => {
    const groupMap = new Map<number, { group: EstateGroup; buildings: Map<number | null, FlatEstate[]> }>();
    for (const g of allEstateGroups || []) {
      groupMap.set(g.id!, { group: g, buildings: new Map() });
    }

    // Add buildings that have estate_group_id set (even if they have no estates yet)
    // Coerce to number — CRUD API may return strings for integer foreign keys
    for (const b of allBuildings as TreeBuilding[]) {
      const gId = b.estate_group_id ? Number(b.estate_group_id) : null;
      if (!gId || !groupMap.has(gId)) continue;
      const entry = groupMap.get(gId)!;
      const bId = Number(b.id!);
      if (!entry.buildings.has(bId)) entry.buildings.set(bId, []);
    }

    // Add estates under their building (or null = unassigned) within their group
    for (const e of allEstates) {
      const gId = e.estate_group_id ? Number(e.estate_group_id) : null;
      if (!gId || !groupMap.has(gId)) continue;
      const entry = groupMap.get(gId)!;
      const bId = e.building_id ? Number(e.building_id) : null;
      if (!entry.buildings.has(bId)) entry.buildings.set(bId, []);
      entry.buildings.get(bId)!.push(e);
    }
    return groupMap;
  }, [allEstateGroups, allBuildings, allEstates]);

  const buildingById = useMemo(() => {
    const m = new Map<number, TreeBuilding>();
    for (const b of allBuildings) if (b.id) m.set(Number(b.id), b as TreeBuilding);
    return m;
  }, [allBuildings]);

  // ── Tree search: matches groups, buildings AND estates ──────────────────
  // All three levels are already fetched in full on mount (allEstateGroups,
  // allBuildings, allEstates via useAllEstatesFlat), so this is a pure
  // client-side scan — no extra requests are made while typing.
  // When a match is found below the group level, the group (and, for an
  // estate match, its building too) is flagged for auto-expand so the tree
  // reveals the path down to the match.
  const treeSearchInfo = useMemo(() => {
    const q = treeSearch.trim().toLowerCase();
    const matchedGroupIds = new Set<number>();
    const matchedBuildingIds = new Set<number>();
    const matchedEstateIds = new Set<number>();
    const autoExpandGroupIds = new Set<number>();
    const autoExpandBuildingIds = new Set<number>();
    const visibleGroupIds = new Set<number>();
    let orphanBuildingsMatch = false;

    if (!q) {
      return {
        active: false, q, matchedGroupIds, matchedBuildingIds, matchedEstateIds,
        autoExpandGroupIds, autoExpandBuildingIds, visibleGroupIds, orphanBuildingsMatch,
      };
    }

    for (const g of allEstateGroups || []) {
      if (g.id == null) continue;
      const gId = g.id;
      const gNameMatch = (g.name || '').toLowerCase().includes(q);
      if (gNameMatch) matchedGroupIds.add(gId);

      let groupHasDescendantMatch = false;
      const groupData = treeData.get(gId);
      if (groupData) {
        for (const [bId, estates] of groupData.buildings) {
          let buildingHasMatchingEstate = false;
          for (const e of estates) {
            if (e.id != null && (e.name || '').toLowerCase().includes(q)) {
              matchedEstateIds.add(e.id);
              buildingHasMatchingEstate = true;
              groupHasDescendantMatch = true;
            }
          }
          if (bId != null) {
            const bNameMatch = (buildingById.get(bId)?.name || '').toLowerCase().includes(q);
            if (bNameMatch) {
              matchedBuildingIds.add(bId);
              groupHasDescendantMatch = true;
            }
            if (buildingHasMatchingEstate) autoExpandBuildingIds.add(bId);
          }
        }
      }

      if (gNameMatch || groupHasDescendantMatch) visibleGroupIds.add(gId);
      if (groupHasDescendantMatch) autoExpandGroupIds.add(gId);
    }

    // Standalone buildings (no estate_group_id) — match by name only, they
    // don't show estates in this tree.
    for (const b of allBuildings as TreeBuilding[]) {
      if (b.estate_group_id) continue;
      if ((b.name || '').toLowerCase().includes(q)) {
        if (b.id != null) matchedBuildingIds.add(Number(b.id));
        orphanBuildingsMatch = true;
      }
    }

    return {
      active: true, q, matchedGroupIds, matchedBuildingIds, matchedEstateIds,
      autoExpandGroupIds, autoExpandBuildingIds, visibleGroupIds, orphanBuildingsMatch,
    };
  }, [treeSearch, allEstateGroups, allBuildings, treeData, buildingById]);

  const filteredGroups = useMemo(() => {
    if (!treeSearchInfo.active) return allEstateGroups || [];
    return (allEstateGroups || []).filter(g => g.id != null && treeSearchInfo.visibleGroupIds.has(g.id));
  }, [allEstateGroups, treeSearchInfo]);

  // Highlights the portion of `text` matching the active search query.
  const renderHighlighted = useCallback((text: string) => {
    if (!treeSearchInfo.active || !text) return text;
    const lower = text.toLowerCase();
    const idx = lower.indexOf(treeSearchInfo.q);
    if (idx === -1) return text;
    return (
      <>
        {text.slice(0, idx)}
        <HighlightMark>{text.slice(idx, idx + treeSearchInfo.q.length)}</HighlightMark>
        {text.slice(idx + treeSearchInfo.q.length)}
      </>
    );
  }, [treeSearchInfo]);

  // ── Admin check ───────────────────────────────────────────────────────────
  useEffect(() => {
    isUserAdmin().then(setIsAdmin).catch(() => {});
  }, []);

  useEffect(() => {
    if (hikEnabled && !hikLoaded && !hikLoading) loadHikLevels();
  }, [hikEnabled]);

  const loadHikLevels = async () => {
    setHikLoading(true);
    try {
      const r = await directApiInstance({ url: '/hik/access-levels', method: 'GET' }) as any;
      setHikLevels(r?.data?.levels || r?.levels || []);
      setHikLoaded(true);
    } catch { setHikLevels([]); }
    finally { setHikLoading(false); }
  };

  // ── Tree toggle ───────────────────────────────────────────────────────────
  const toggle = useCallback((key: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpanded(prev => {
      const n = new Set(prev);
      n.has(key) ? n.delete(key) : n.add(key);
      return n;
    });
  }, []);

  const select = useCallback((type: SelectionType, id: number) => {
    setSelection({ type, id });
    // Auto-open edit panel
  }, []);

  // ── Forms ─────────────────────────────────────────────────────────────────
  const [groupForm, setGroupForm] = useState<any>({ name: '', hik_access_level_id: null });
  const [groupImg, setGroupImg] = useState<File | null>(null);
  const [groupImgPreview, setGroupImgPreview] = useState<string | null>(null);
  const [groupImgName, setGroupImgName] = useState<string | null>(null);
  const groupImgRef = useRef<HTMLInputElement>(null);

  // Panel view/edit toggles
  const [groupEditMode, setGroupEditMode] = useState(false);
  const [buildingEditMode, setBuildingEditMode] = useState(false);
  const [buildingAutoAssign, setBuildingAutoAssign] = useState(false);

  // Original snapshots for dirty-checking
  const [originalGroupForm, setOriginalGroupForm] = useState<any>(null);
  const [originalBuildingForm, setOriginalBuildingForm] = useState<any>(null);
  const [originalEstateForm, setOriginalEstateForm] = useState<any>(null);

  // Panel-level search states
  const [groupBuildingSearch, setGroupBuildingSearch] = useState('');
  const [buildingEstateSearch, setBuildingEstateSearch] = useState('');
  const [showMatchPopover, setShowMatchPopover] = useState(false);
  const [hikSearch, setHikSearch] = useState('');

  const [buildingForm, setBuildingForm] = useState<any>({ name: '', description: '', estate_group_id: null, hik_access_level_id: null });

  const [estateForm, setEstateForm] = useState<any>({
    name: '', estate_group_id: null, building_id: null,
    estate_type_id: null, status: '', hik_access_level_id: null, show_in_app: true,
  });

  // ── Load form when selection changes ─────────────────────────────────────
  useEffect(() => {
    setHikSectionOpen(false);
    setHikSearch('');
    setGroupBuildingSearch('');
    setBuildingEstateSearch('');
    setShowMatchPopover(false);
    setGroupEditMode(false);
    setBuildingEditMode(false);
    setBuildingAutoAssign(false);
    setOriginalGroupForm(null);
    setOriginalBuildingForm(null);
    setOriginalEstateForm(null);
    if (!selection.id) return;

    if (selection.type === 'group') {
      const g = (allEstateGroups || []).find(x => x.id === selection.id);
      if (g) {
        const form = { name: g.name || '', hik_access_level_id: (g as any).hik_access_level_id || null };
        setGroupForm(form);
        setOriginalGroupForm(form);
        setGroupImgPreview(null);
        setGroupImg(null);
        setGroupImgName(null);
        if (groupImgRef.current) groupImgRef.current.value = '';
        const url = (g as any).home_url;
        if (url) getImageUrl(url).then(setGroupImgPreview).catch(() => {});
      }
    }

    if (selection.type === 'building') {
      const b = buildingById.get(selection.id) as TreeBuilding | undefined;
      if (b) {
        const form = {
          name: b.name || '',
          description: b.description || '',
          estate_group_id: b.estate_group_id || null,
          hik_access_level_id: b.hik_access_level_id || null,
        };
        setBuildingForm(form);
        setOriginalBuildingForm(form);
      }
    }

    if (selection.type === 'estate') {
      const e = allEstates.find(x => x.id === selection.id);
      if (e) {
        const form = {
          name: e.name || '',
          estate_group_id: e.estate_group_id || null,
          building_id: e.building_id || null,
          estate_type_id: e.estate_type_id || null,
          status: e.status || '',
          hik_access_level_id: e.hik_access_level_id || null,
          show_in_app: (e as any).show_in_app ?? true,
        };
        setEstateForm(form);
        setOriginalEstateForm(form);
      }
    }
  }, [selection.id, selection.type]);

  // ── Submit handlers ───────────────────────────────────────────────────────
  const saveGroup = async () => {
    const g = (allEstateGroups || []).find(x => x.id === selection.id);
    if (!groupForm.name?.trim()) { toaster.negative('Name is required', { autoHideDuration: 3000 }); return; }
    try {
      let home_url = (g as any)?.home_url || null;
      if (groupImg) {
        const fd = new FormData();
        fd.append('file', groupImg);
        // SAM-949: the authenticated image API of this environment (it was a fixed dev URL with no credentials).
        const r = await fetch(imageApiUrl(), { method: 'POST', headers: imageApiHeaders(), body: fd });
        if (!r.ok) throw new Error('Image upload failed');
        home_url = uploadedImageKey(await r.json());
        if (!home_url) throw new Error('Image upload failed');
      }
      const payload: any = { name: groupForm.name, tenant_id: currentTenantId, home_url };
      if (hikEnabled) payload.hik_access_level_id = groupForm.hik_access_level_id || null;
      if (selection.id) {
        await patchEstateGroup(selection.id.toString(), payload);
        setOriginalGroupForm({ name: payload.name, hik_access_level_id: payload.hik_access_level_id ?? null });
        setGroupImg(null);
        setGroupImgName(null);
      } else {
        const res: any = await createEstateGroup(payload);
        const newId = res?.data?.[0]?.id;
        if (newId) setSelection({ type: 'group', id: newId });
      }
      queryClient.invalidateQueries({ queryKey: ['estates-flat-tree'] });
    } catch (err: any) { toaster.negative(err?.message || 'Error saving group', { autoHideDuration: 3000 }); }
  };

  const saveBuilding = async () => {
    if (!buildingForm.name?.trim()) { toaster.negative('Name is required', { autoHideDuration: 3000 }); return; }
    if (!buildingForm.estate_group_id) { toaster.negative('Estate Group is required', { autoHideDuration: 3000 }); return; }

    // Capture before any awaits so the closure values are stable
    const shouldAutoAssign = buildingAutoAssign && unassigned.length > 0;
    const estateIdsToAssign = unassigned.map(e => e.id);

    const payload: any = {
      name: buildingForm.name.trim(),
      description: buildingForm.description || null,
      estate_group_id: buildingForm.estate_group_id,
    };
    if (hikEnabled) payload.hik_access_level_id = buildingForm.hik_access_level_id || null;
    try {
      if (selection.id) {
        await patchBuilding(selection.id.toString(), payload);
        setOriginalBuildingForm({ ...buildingForm, ...payload });
      } else {
        const res: any = await createBuilding(payload);
        // POST /building returns { data: Building[] } — new record is first element
        const newId = res?.data?.[0]?.id;
        if (newId) {
          if (shouldAutoAssign && estateIdsToAssign.length > 0) {
            await bulkAssign.mutateAsync({ estateIds: estateIdsToAssign, buildingId: Number(newId) });
          }
          setSelection({ type: 'building', id: Number(newId) });
          setBuildingAutoAssign(false);
        }
      }
      queryClient.invalidateQueries({ queryKey: ['estates-flat-tree'] });
    } catch (err: any) {
      toaster.negative(err?.response?.data?.message || err?.message || 'Error saving building', { autoHideDuration: 3000 });
    }
  };

  const saveEstate = async () => {
    if (!estateForm.name?.trim()) { toaster.negative('Name is required', { autoHideDuration: 3000 }); return; }
    if (!estateForm.estate_group_id) { toaster.negative('Estate Group is required', { autoHideDuration: 3000 }); return; }
    if (!estateForm.status) { toaster.negative('Status is required', { autoHideDuration: 3000 }); return; }
    try {
      if (selection.id) {
        const clean = Object.entries(estateForm).reduce((acc, [k, v]) => {
          if (v === undefined) return acc;
          if (v === null && k !== 'hik_access_level_id' && k !== 'building_id') return acc;
          if (v !== null && typeof v === 'object') return acc;
          acc[k] = v; return acc;
        }, {} as any);
        await updateEstate(selection.id.toString(), clean);
        setOriginalEstateForm({ ...estateForm });
      } else {
        const res: any = await createEstate(estateForm);
        // POST /estate returns { data: Estate[] } — new record is first element
        const newId = res?.data?.[0]?.id;
        if (newId) setSelection({ type: 'estate', id: newId });
      }
      queryClient.invalidateQueries({ queryKey: ['estates-flat-tree'] });
    } catch {}
  };

  const deleteSelected = () => {
    if (!selection.id) return;
    const title =
      selection.type === 'group' ? 'Delete Estate Group' :
      selection.type === 'building' ? 'Delete Building' : 'Delete Estate';
    const body =
      selection.type === 'group' ? 'This will permanently delete the estate group. This action cannot be undone.' :
      selection.type === 'building' ? 'This will delete the building. All assigned estates will be unlinked but not deleted.' :
      'This will permanently delete the estate. This action cannot be undone.';
    openConfirm(title, body, async () => {
      try {
        if (selection.type === 'group') await deleteEstateGroup(selection.id!.toString());
        if (selection.type === 'building') await deleteBuilding(selection.id!.toString());
        if (selection.type === 'estate') await deleteEstate(selection.id!.toString());
        setSelection({ type: null, id: null });
        queryClient.invalidateQueries({ queryKey: ['estates-flat-tree'] });
      } catch {}
    }, true);
  };

  // ── Bulk assign preview — uses buildingForm.name so it works for both new and existing ──
  const matchedEstates = useMemo(() => {
    const name = buildingForm.name?.trim();
    if (!name || name.length < 2) return [];
    return allEstates.filter(e => e.name?.toLowerCase().startsWith(name.toLowerCase()));
  }, [buildingForm.name, allEstates]);
  const unassigned = matchedEstates.filter(e => !e.building_id);

  // ── Hik level name helper ─────────────────────────────────────────────────
  const hikName = (id: string | null | undefined) =>
    id ? (hikLevels.find(l => l.id === id)?.name || id) : null;

  // ── Dirty flags — Save button only enabled when something changed ─────────
  const groupDirty = useMemo(() => {
    if (!selection.id) return true;
    if (!originalGroupForm) return false;
    return JSON.stringify(groupForm) !== JSON.stringify(originalGroupForm) || !!groupImg;
  }, [groupForm, originalGroupForm, groupImg, selection.id]);

  const buildingDirty = useMemo(() => {
    if (!selection.id) return true;
    if (!originalBuildingForm) return false;
    return JSON.stringify(buildingForm) !== JSON.stringify(originalBuildingForm);
  }, [buildingForm, originalBuildingForm, selection.id]);

  const estateDirty = useMemo(() => {
    if (!selection.id) return true;
    if (!originalEstateForm) return false;
    return JSON.stringify(estateForm) !== JSON.stringify(originalEstateForm);
  }, [estateForm, originalEstateForm, selection.id]);

  // ─── Users associated with selected estate ────────────────────────────
  const {
    userEstates: estateUsers,
    isLoading: isLoadingEstateUsers,
  } = useUserEstate({
    estate_id_in: selection.type === 'estate' && selection.id ? String(selection.id) : undefined,
    enabled: selection.type === 'estate' && !!selection.id,
  });

  // ─── User estate counts for building estate list ──────────────────────
  const buildingEstateIds = useMemo(() => {
    if (selection.type !== 'building' || !selection.id) return [];
    return allEstates.filter(e => e.building_id === selection.id).map(e => e.id!).filter(Boolean);
  }, [selection.type, selection.id, allEstates]);

  const { userEstates: buildingUserEstates } = useUserEstate({
    estate_id_in: buildingEstateIds.length > 0 ? buildingEstateIds.join(',') : undefined,
    enabled: selection.type === 'building' && buildingEstateIds.length > 0,
    limit: 200,
  });

  const estateUserCountMap = useMemo(() => {
    const map: Record<number, number> = {};
    for (const ue of buildingUserEstates) {
      if (ue.estate_id) map[ue.estate_id] = (map[ue.estate_id] || 0) + 1;
    }
    return map;
  }, [buildingUserEstates]);

  const estateUserColumns = useMemo<MRT_ColumnDef<UserEstate>[]>(() => [
    {
      accessorKey: 'user.name',
      header: 'Name',
      Cell: ({ row }) => {
        const user = row.original?.user as any;
        const fullName = [user?.name, user?.last_name].filter(Boolean).join(' ') || '--';
        const userId = user?.id;
        return userId ? (
          <span
            style={{ color: 'var(--tenant-primary)', cursor: 'pointer', fontWeight: 500 }}
            onClick={() => navigate(`/users/${userId}`)}
          >
            {fullName}
          </span>
        ) : <span>{fullName}</span>;
      },
    },
    {
      accessorKey: 'user.email',
      header: 'Email',
      Cell: ({ row }) => (row.original?.user as any)?.email || '--',
    },
    {
      accessorKey: 'user_type.name',
      header: 'User Type',
      Cell: ({ row }) => row.original?.user_type?.name || '--',
    },
    {
      accessorKey: 'user_estate_status.name',
      header: 'Status',
      size: 130,
      Cell: ({ row }) => {
        const status = String(row.original?.user_estate_status?.name || 'Unknown').toLowerCase();
        return (
          <div style={{
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: '500',
            backgroundColor: status === 'active' ? '#E8F5E8' : status === 'suspended' ? '#FDE8E8' : '#F0F0F0',
            color: status === 'active' ? '#2D7738' : status === 'suspended' ? '#C53030' : '#666',
          }}>
            {row.original?.user_estate_status?.name || 'Unknown'}
          </div>
        );
      },
    },
    {
      id: 'expiration_date',
      header: 'Expiration Date',
      size: 140,
      enableSorting: false,
      Cell: ({ row }) => (
        <div onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()} style={{ width: '100%' }}>
          <UserEstateExpirationDate userEstate={row.original} disabled={!canEditResidency} />
        </div>
      ),
    },
    {
      id: 'change_status',
      header: 'Change Status',
      size: 160,
      enableSorting: false,
      Cell: ({ row }) => (
        <UserEstateStatusSelector userEstate={row.original} disabled={!canChangeResidencyStatus} />
      ),
    },
  ], [navigate, canEditResidency, canChangeResidencyStatus]);

  // ═════════════════════════════════════════════════════════════════════════
  // RENDER
  // ═════════════════════════════════════════════════════════════════════════

  // ─── Selected entity data ──────────────────────────────────────────────
  const selectedGroup = selection.type === 'group' ? (allEstateGroups || []).find(g => g.id === selection.id) : null;
  const selectedBuilding = selection.type === 'building' ? buildingById.get(selection.id!) : null;
  const selectedEstate = selection.type === 'estate' ? allEstates.find(e => e.id === selection.id) : null;

  // Derived estate's group for breadcrumb
  const estateGroup = selectedEstate
    ? (allEstateGroups || []).find(g => g.id === selectedEstate.estate_group_id)
    : null;
  const estateBuilding = selectedEstate?.building_id ? buildingById.get(selectedEstate.building_id) : null;
  const buildingGroup = selectedBuilding
    ? (allEstateGroups || []).find(g => g.id === Number((selectedBuilding as TreeBuilding).estate_group_id)) ?? null
    : null;

  // ─── HikConnect selector — collapsible, with search ─────────────────
  // embedded=true renders as a divider section (no outer FormCard) for use inside another card
  const renderHikSelector = (
    currentValue: string | null,
    onChange: (v: string | null) => void,
    label?: string,
    embedded = false
  ) => {
    const currentName = hikName(currentValue);
    const filteredLevels = (hikSearch.trim()
      ? hikLevels.filter(l =>
          l.name?.toLowerCase().includes(hikSearch.toLowerCase()) ||
          l.remark?.toLowerCase().includes(hikSearch.toLowerCase())
        )
      : [...hikLevels]
    ).sort((a, b) =>
      (a.name ?? '').localeCompare(b.name ?? '', undefined, { numeric: true, sensitivity: 'base' })
    );

    const header = (
      <div
        style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', userSelect: 'none' }}
        onClick={() => {
          if (!hikSectionOpen && !hikLoaded && !hikLoading) loadHikLevels();
          if (hikSectionOpen) setHikSearch('');
          setHikSectionOpen(o => !o);
        }}
      >
        <DoorOpen size={14} color="#6B7280" style={{ flexShrink: 0 }} />
        <span style={{ fontSize: '13px', fontWeight: 600, color: '#374151', flex: 1 }}>
          {label || 'Hik-Connect Access Level'}
        </span>
        {currentValue
          ? <HikBadge $level="estate">{currentName || currentValue}</HikBadge>
          : <span style={{ fontSize: '12px', color: '#9CA3AF' }}>Default</span>
        }
        <ChevronDown
          size={14}
          color="#6B7280"
          style={{ flexShrink: 0, transition: 'transform 0.15s', transform: hikSectionOpen ? 'rotate(0deg)' : 'rotate(-90deg)' }}
        />
      </div>
    );

    const body = hikSectionOpen && (
      <div style={{ marginTop: '12px' }}>
        {/* Search + refresh row */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '6px', backgroundColor: '#F3F4F6', borderRadius: '6px', padding: '6px 10px' }}>
            <Search size={12} color="#9CA3AF" />
            <input
              value={hikSearch}
              onChange={e => setHikSearch(e.target.value)}
              placeholder="Search access levels..."
              style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: '12px', color: '#374151', flex: 1 }}
              onClick={e => e.stopPropagation()}
            />
          </div>
          <Button
            onClick={(e: any) => { e.stopPropagation(); loadHikLevels(); }}
            size="mini"
            kind="secondary"
            isLoading={hikLoading}
            startEnhancer={() => <RefreshCw size={11} />}
            overrides={{ BaseButton: { style: { borderRadius: '6px', fontSize: '11px', paddingLeft: '8px', paddingRight: '8px' } } }}
          >
            {hikLevels.length > 0 ? 'Refresh' : 'Load'}
          </Button>
        </div>

        {hikLevels.length === 0 && !hikLoading ? (
          <div style={{ padding: '12px', textAlign: 'center', color: '#9CA3AF', fontSize: '12px', border: '1px dashed #E5E7EB', borderRadius: '8px' }}>
            Click "Load" to fetch available access levels
          </div>
        ) : (
          <div style={{ border: '1px solid #E5E7EB', borderRadius: '8px', overflow: 'hidden', maxHeight: '200px', overflowY: 'auto' }}>
            {!hikSearch && (
              <AccessLevelRow $selected={!currentValue} onClick={() => onChange(null)}>
                <AccessLevelDot $on={!currentValue}>
                  {!currentValue && <svg width="9" height="7" viewBox="0 0 9 7" fill="none"><path d="M1 3.5L3.5 6L8 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                </AccessLevelDot>
                <span style={{ fontSize: '13px', color: '#374151', fontWeight: !currentValue ? 600 : 400 }}>Use parent default</span>
              </AccessLevelRow>
            )}
            {filteredLevels.map((level: any, idx: number) => {
              const on = currentValue === level.id;
              const devices = (level.associateResList || []).map((d: any) => d.name).join(', ');
              return (
                <div key={level.id} style={{ borderTop: (!hikSearch || idx > 0) ? '1px solid #F3F4F6' : 'none' }}>
                  <AccessLevelRow $selected={on} onClick={() => onChange(level.id)}>
                    <AccessLevelDot $on={on}>
                      {on && <svg width="9" height="7" viewBox="0 0 9 7" fill="none"><path d="M1 3.5L3.5 6L8 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                    </AccessLevelDot>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '13px', fontWeight: on ? 600 : 400, color: '#111827' }}>{level.name}</div>
                      {(level.remark || devices) && (
                        <div style={{ fontSize: '11px', color: '#9CA3AF', marginTop: '1px', display: 'flex', gap: '8px' }}>
                          {level.remark && <span>{level.remark}</span>}
                          {devices && <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}><DoorOpen size={10} />{devices}</span>}
                        </div>
                      )}
                    </div>
                  </AccessLevelRow>
                </div>
              );
            })}
            {filteredLevels.length === 0 && hikSearch && (
              <div style={{ padding: '12px', textAlign: 'center', color: '#9CA3AF', fontSize: '12px' }}>
                No levels match "{hikSearch}"
              </div>
            )}
          </div>
        )}
      </div>
    );

    if (embedded) {
      return (
        <div style={{ borderTop: '1px solid #F3F4F6', paddingTop: '14px', marginTop: '4px' }}>
          {header}
          {body}
        </div>
      );
    }

    return (
      <FormCard>
        {header}
        {body}
      </FormCard>
    );
  };

  // FormControl / Input / Select type-cast helpers
  const FC = FormControl as any;
  const Inp = Input as any;
  const Sel = Select as any;

  // ─── Left tree render ─────────────────────────────────────────────────
  const renderTree = () => (
    <TreeScroll>
      {filteredGroups.map(group => {
        const gKey = `g-${group.id}`;
        const gOpen = expanded.has(gKey) || (treeSearchInfo.active && !!group.id && treeSearchInfo.autoExpandGroupIds.has(group.id));
        const groupData = treeData.get(group.id!);
        const groupEstateCount = allEstates.filter(e => e.estate_group_id === group.id).length;
        const hikLevel = (group as any).hik_access_level_id;

        // Unique buildings in this group
        const buildingIds = new Set<number | null>();
        groupData?.buildings.forEach((_, bId) => buildingIds.add(bId));
        const buildingCount = [...buildingIds].filter(Boolean).length;

        return (
          <div key={group.id}>
            {/* Group row */}
            <TreeRow
              $selected={selection.type === 'group' && selection.id === group.id}
              $depth={0}
              onClick={() => select('group', group.id!)}
            >
              <div onClick={(e) => toggle(gKey, e)} style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                <Chevron $open={gOpen}>
                  <ChevronDown size={14} color="#6B7280" />
                </Chevron>
              </div>
              <Layers size={14} color="#6B7280" style={{ flexShrink: 0 }} />
              <TreeLabel $bold>{renderHighlighted(group.name || '')}</TreeLabel>
              <TreeBadge>{groupEstateCount}</TreeBadge>
              {hikLevel && <div style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#059669', flexShrink: 0 }} />}
            </TreeRow>

            {/* Group children */}
            {gOpen && (
              <div>
                {/* Buildings in group — sorted alphabetically (accent/case-insensitive, numeric-aware) */}
                {[...buildingIds].filter(Boolean).sort((a, b) => {
                  const na = buildingById.get(a as number)?.name ?? '';
                  const nb = buildingById.get(b as number)?.name ?? '';
                  return na.localeCompare(nb, undefined, { numeric: true, sensitivity: 'base' });
                }).map(bId => {
                  const bKey = `b-${bId}`;
                  const bOpen = expanded.has(bKey) || (treeSearchInfo.active && bId != null && treeSearchInfo.autoExpandBuildingIds.has(bId as number));
                  const building = buildingById.get(bId as number);
                  const bEstates = (groupData?.buildings.get(bId) || [])
                    .slice()
                    .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '', undefined, { numeric: true, sensitivity: 'base' }));
                  const bHik = (building as any)?.hik_access_level_id;

                  return (
                    <div key={bId}>
                      <TreeRow
                        $selected={selection.type === 'building' && selection.id === bId}
                        $depth={1}
                        onClick={() => select('building', bId as number)}
                      >
                        <div onClick={(e) => toggle(bKey, e)} style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                          <Chevron $open={bOpen}>
                            <ChevronDown size={13} color="#6B7280" />
                          </Chevron>
                        </div>
                        <Building2 size={13} color="#6B7280" style={{ flexShrink: 0 }} />
                        <TreeLabel $bold={false}>{renderHighlighted(building?.name || `Building ${bId}`)}</TreeLabel>
                        <TreeBadge>{bEstates.length}</TreeBadge>
                        {bHik && <div style={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: '#059669', flexShrink: 0 }} />}
                      </TreeRow>

                      {/* Estates in building */}
                      {bOpen && bEstates.map(estate => (
                        <TreeRow
                          key={estate.id}
                          $selected={selection.type === 'estate' && selection.id === estate.id}
                          $depth={2}
                          onClick={() => select('estate', estate.id)}
                        >
                          <Home size={12} color="#9CA3AF" style={{ flexShrink: 0 }} />
                          <TreeLabel $bold={false} style={{ color: '#4B5563' }}>{renderHighlighted(estate.name || '')}</TreeLabel>
                          {estate.hik_access_level_id && (
                            <div style={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: '#2563EB', flexShrink: 0 }} />
                          )}
                        </TreeRow>
                      ))}
                    </div>
                  );
                })}

                {/* Estates without a building — shown directly under the group */}
                {(groupData?.buildings.get(null) || [])
                  .slice()
                  .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '', undefined, { numeric: true, sensitivity: 'base' }))
                  .map(estate => (
                  <TreeRow
                    key={estate.id}
                    $selected={selection.type === 'estate' && selection.id === estate.id}
                    $depth={1}
                    onClick={() => select('estate', estate.id)}
                  >
                    <Home size={12} color="#9CA3AF" style={{ flexShrink: 0 }} />
                    <TreeLabel $bold={false} style={{ color: '#4B5563' }}>{renderHighlighted(estate.name || '')}</TreeLabel>
                    {estate.hik_access_level_id && (
                      <div style={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: '#2563EB', flexShrink: 0 }} />
                    )}
                  </TreeRow>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {/* Orphan buildings (not in any group via estates) */}
      {(() => {
        // A building is standalone only when it has no estate_group_id assigned
        const orphans = allBuildings
          .filter(b => !(b as TreeBuilding).estate_group_id)
          .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '', undefined, { numeric: true, sensitivity: 'base' }));
        // While searching, only standalone buildings are relevant here — a group
        // name/estate match can't live under this section.
        const visibleOrphans = treeSearchInfo.active
          ? orphans.filter(b => b.id != null && treeSearchInfo.matchedBuildingIds.has(Number(b.id)))
          : orphans;
        if (orphans.length === 0) return null;
        if (treeSearchInfo.active && visibleOrphans.length === 0) return null;
        const oKey = 'orphan-buildings';
        const oOpen = expanded.has(oKey) || (treeSearchInfo.active && treeSearchInfo.orphanBuildingsMatch);
        return (
          <div>
            <TreeRow $selected={false} $depth={0} onClick={(e) => toggle(oKey, e)}>
              <Chevron $open={oOpen}><ChevronDown size={14} color="#D1D5DB" /></Chevron>
              <Building2 size={14} color="#D1D5DB" style={{ flexShrink: 0 }} />
              <TreeLabel $bold style={{ color: '#9CA3AF' }}>Standalone Buildings</TreeLabel>
              <TreeBadge>{visibleOrphans.length}</TreeBadge>
            </TreeRow>
            {oOpen && visibleOrphans.map(b => (
              <TreeRow
                key={b.id}
                $selected={selection.type === 'building' && selection.id === b.id}
                $depth={1}
                onClick={() => select('building', b.id!)}
              >
                <Building2 size={13} color="#6B7280" style={{ flexShrink: 0, marginLeft: 16 }} />
                <TreeLabel $bold={false}>{renderHighlighted(b.name || '')}</TreeLabel>
              </TreeRow>
            ))}
          </div>
        );
      })()}
    </TreeScroll>
  );

  // ─── Right panel per type ───────────────────────────────────────────────
  const renderRight = () => {
    if (!selection.type) return (
      <EmptyState>
        <Layers size={40} color="#E5E7EB" />
        <div style={{ fontSize: '15px', color: '#9CA3AF' }}>{selectHint(canEditEstates)}</div>
        {isAdmin && (
          <Button
            onClick={() => setSelection({ type: 'group', id: null })}
            kind="secondary"
            overrides={{ BaseButton: { style: { marginTop: '8px' } } }}
          >
            + New Estate Group
          </Button>
        )}
      </EmptyState>
    );

    if (selection.type === 'group') return renderGroupPanel();
    if (selection.type === 'building') return renderBuildingPanel();
    if (selection.type === 'estate') return renderEstatePanel();
    return null;
  };

  // ─── GROUP PANEL ─────────────────────────────────────────────────────
  const renderGroupPanel = () => {
    const buildings = allBuildings
      .filter(b => {
        const gId = (b as TreeBuilding).estate_group_id;
        return gId && Number(gId) === selection.id;
      })
      .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '', undefined, { numeric: true, sensitivity: 'base' }));
    const estateCount = allEstates.filter(e => e.estate_group_id === selection.id).length;
    const unassignedCount = allEstates.filter(e => e.estate_group_id === selection.id && !e.building_id).length;
    const filteredBuildings = groupBuildingSearch.trim()
      ? buildings.filter(b => b.name?.toLowerCase().includes(groupBuildingSearch.toLowerCase()))
      : buildings;
    const isEditing = !selection.id || groupEditMode;

    return (
      <>
        <RightHeader>
          <Breadcrumb>
            <Crumb $clickable={false}>Estate Groups</Crumb>
            <span>/</span>
            <Crumb $clickable={false}>{selectedGroup?.name || (selection.id ? 'Group' : 'New Group')}</Crumb>
          </Breadcrumb>
          <RightPanelTitle>
            <Layers size={20} color="#6B7280" />
            <span style={{ flex: 1 }}>{selection.id ? (selectedGroup?.name || 'Group') : 'New Estate Group'}</span>
            {/* Edit / Cancel-edit toggle */}
            {selection.id && isAdmin && !isEditing && (
              <button
                onClick={() => setGroupEditMode(true)}
                style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '5px 12px', borderRadius: '8px', border: '1px solid #E5E7EB', background: '#fff', cursor: 'pointer', fontSize: '13px', color: '#374151', fontWeight: 500 }}
              >
                <Pencil size={13} color="#6B7280" /> Edit
              </button>
            )}
            {selection.id && isEditing && (
              <button
                onClick={() => {
                  setGroupEditMode(false);
                  // Revert to original
                  if (originalGroupForm) setGroupForm(originalGroupForm);
                  setGroupImg(null); setGroupImgName(null);
                  if (groupImgRef.current) groupImgRef.current.value = '';
                }}
                style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '5px 10px', borderRadius: '8px', border: '1px solid #E5E7EB', background: '#fff', cursor: 'pointer', fontSize: '13px', color: '#9CA3AF' }}
              >
                <X size={13} /> Cancel edit
              </button>
            )}
          </RightPanelTitle>
        </RightHeader>

        <RightScroll>
          {/* ── Detail / Edit card ─── */}
          <FormCard>
            {!isEditing ? (
              /* ── VIEW MODE ── */
              <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                <div style={{ width: 80, height: 80, borderRadius: 12, overflow: 'hidden', flexShrink: 0, backgroundColor: '#F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {groupImgPreview
                    ? <img src={groupImgPreview} alt="cover" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : <Layers size={28} color="#D1D5DB" />
                  }
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '18px', fontWeight: 700, color: '#111827', marginBottom: '6px' }}>{selectedGroup?.name}</div>
                  {showHik && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <DoorOpen size={13} color="#9CA3AF" />
                    <span style={{ fontSize: '12px', color: '#9CA3AF' }}>Hik-Connect:</span>
                    {groupForm.hik_access_level_id
                      ? <HikBadge $level="group">{hikName(groupForm.hik_access_level_id) || groupForm.hik_access_level_id}</HikBadge>
                      : <span style={{ fontSize: '12px', color: '#D1D5DB' }}>Default (inherited)</span>
                    }
                  </div>
                  )}
                </div>
              </div>
            ) : (
              /* ── EDIT MODE ── */
              <>
                <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-start', marginBottom: '14px' }}>
                  <div
                    style={{ width: 72, height: 72, borderRadius: 10, overflow: 'hidden', border: '2px dashed #E5E7EB', flexShrink: 0, backgroundColor: '#F9FAFB', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    onClick={() => groupImgRef.current?.click()}
                  >
                    {groupImgPreview
                      ? <img src={groupImgPreview} alt="cover" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      : <Layers size={24} color="#D1D5DB" />
                    }
                    <input ref={groupImgRef} type="file" accept="image/*" style={{ display: 'none' }}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        setGroupImg(file); setGroupImgName(file.name);
                        setGroupImgPreview(URL.createObjectURL(file));
                      }}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <FC label="Group Name" overrides={{ Label: { style: { fontSize: '12px' } } }}>
                      <Inp
                        value={groupForm.name || ''}
                        onChange={(e: any) => setGroupForm((p: any) => ({ ...p, name: e.target.value }))}
                        placeholder="e.g. Group Name"
                      />
                    </FC>
                    <div style={{ fontSize: '11px', color: 'var(--tenant-primary, #6B7280)', cursor: 'pointer', marginTop: '-2px' }}
                      onClick={() => groupImgRef.current?.click()}>
                      {groupImgName || (groupImgPreview ? 'Change image' : '+ Add cover image')}
                    </div>
                  </div>
                </div>
                {/* HIK inline in edit mode */}
                {showHik && renderHikSelector(
                  groupForm.hik_access_level_id,
                  (v) => setGroupForm((p: any) => ({ ...p, hik_access_level_id: v })),
                  'Hik-Connect Access Level (group fallback)',
                  true
                )}
              </>
            )}
          </FormCard>

          {/* ── Stats chips ─── */}
          {selection.id && (
            <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
              {[
                { label: 'Estates', value: estateCount, color: '#111827' },
                { label: 'Buildings', value: buildings.length, color: '#111827' },
              ].map(s => (
                <div key={s.label} style={{ flex: 1, padding: '10px 12px', backgroundColor: '#F9FAFB', borderRadius: '8px', border: '1px solid #E5E7EB', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <div style={{ fontSize: '20px', fontWeight: 700, color: s.color, lineHeight: 1 }}>{s.value}</div>
                  <div style={{ fontSize: '11px', color: '#9CA3AF', marginTop: '3px' }}>{s.label}</div>
                </div>
              ))}
            </div>
          )}

          {/* ── Buildings section with search ─── */}
          {selection.id && (
            <FormCard>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: buildings.length > 0 ? '12px' : '0' }}>
                <Building2 size={14} color="#6B7280" />
                <span style={{ fontSize: '13px', fontWeight: 600, color: '#374151', flex: 1 }}>
                  Buildings {buildings.length > 0 && `(${buildings.length})`}
                </span>
                {buildings.length > 3 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', backgroundColor: '#F3F4F6', borderRadius: '6px', padding: '4px 8px' }}>
                    <Search size={12} color="#9CA3AF" />
                    <input value={groupBuildingSearch} onChange={e => setGroupBuildingSearch(e.target.value)}
                      placeholder="Filter buildings..." style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: '12px', color: '#374151', width: '120px' }} />
                  </div>
                )}
                {isAdmin && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: 'var(--tenant-primary, #374151)', cursor: 'pointer', fontWeight: 500 }}
                    onClick={() => { setBuildingForm({ name: '', description: '', estate_group_id: selection.id, hik_access_level_id: null }); setSelection({ type: 'building', id: null }); }}>
                    <Plus size={12} /> Add
                  </div>
                )}
              </div>
              {buildings.length === 0 ? (
                <div style={{ padding: '16px', textAlign: 'center', color: '#9CA3AF', fontSize: '13px', border: '1px dashed #E5E7EB', borderRadius: '8px' }}>
                  {noBuildingsText(isAdmin)}
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
                  {filteredBuildings.map((b, idx) => {
                    const bEstates = allEstates.filter(e => e.building_id === b.id);
                    const bHik = (b as any).hik_access_level_id;
                    return (
                      <div key={b.id}
                        style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 0', borderTop: idx > 0 ? '1px solid #F3F4F6' : 'none', cursor: 'pointer' }}
                        onClick={() => select('building', b.id!)}>
                        <Building2 size={14} color="#9CA3AF" />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '13px', fontWeight: 500, color: '#111827' }}>{b.name}</div>
                          <div style={{ fontSize: '11px', color: '#9CA3AF' }}>{bEstates.length} estates</div>
                        </div>
                        {bHik ? <HikBadge $level="building">{hikName(bHik) || 'HIK'}</HikBadge> : <HikBadge $level="default">Inherit</HikBadge>}
                        <ChevronRight size={14} color="#D1D5DB" />
                      </div>
                    );
                  })}
                  {filteredBuildings.length === 0 && (
                    <div style={{ padding: '12px', textAlign: 'center', color: '#9CA3AF', fontSize: '13px' }}>
                      No buildings match "{groupBuildingSearch}"
                    </div>
                  )}
                </div>
              )}
            </FormCard>
          )}
        </RightScroll>

        <StickyFooter>
          <FooterActions>
            {selection.id && isAdmin && isEditing && (
              <Button kind="tertiary" onClick={deleteSelected} startEnhancer={() => <Trash2 size={13} />}
                overrides={{ BaseButton: { style: { color: '#EF4444', paddingLeft: '10px', paddingRight: '10px' } } }}>
                Delete
              </Button>
            )}
          </FooterActions>
          <FooterActions>
            {isEditing && (
              <>
                <Button kind="tertiary" onClick={() => selection.id ? setGroupEditMode(false) : setSelection({ type: null, id: null })}>Cancel</Button>
                <Button onClick={saveGroup} isLoading={cGroup || uGroup} disabled={!groupDirty} overrides={{ BaseButton: { style: { minWidth: '120px' } } }}>
                  {selection.id ? 'Save Changes' : 'Create Group'}
                </Button>
              </>
            )}
          </FooterActions>
        </StickyFooter>
      </>
    );
  };

  // ─── BUILDING PANEL ───────────────────────────────────────────────────
  const renderBuildingPanel = () => {
    const bEstates = selection.id ? allEstates.filter(e => e.building_id === selection.id) : [];
    const filteredBEstates = buildingEstateSearch.trim()
      ? bEstates.filter(e => e.name?.toLowerCase().includes(buildingEstateSearch.toLowerCase()))
      : bEstates;
    const unassignedPreview = matchedEstates.filter(e => !e.building_id);
    const alreadyAssigned = matchedEstates.filter(e => e.building_id && e.building_id !== selection.id);
    const isEditing = !selection.id || buildingEditMode;

    // Group this building belongs to (via estate_group_id directly)
    const bGroupId = (selectedBuilding as TreeBuilding | null)?.estate_group_id ?? buildingForm.estate_group_id;
    const bGroup = bGroupId ? (allEstateGroups || []).find(g => g.id === bGroupId) : null;

    const groupOptions = (allEstateGroups || []).map(g => ({ id: g.id, label: g.name || `Group ${g.id}` }));
    const selectedGroupForBuilding = groupOptions.filter(o => o.id === buildingForm.estate_group_id);

    return (
      <>
        <RightHeader>
          {bGroup && (
            <Breadcrumb>
              <Crumb $clickable onClick={() => select('group', bGroup.id!)}>
                {bGroup.name}
              </Crumb>
              <span>/</span>
              <Crumb $clickable={false}>{selectedBuilding?.name || 'Building'}</Crumb>
            </Breadcrumb>
          )}
          <RightPanelTitle>
            <Building2 size={20} color="#6B7280" />
            <span style={{ flex: 1 }}>{selection.id ? (selectedBuilding?.name || 'Building') : 'New Building'}</span>
            {/* Edit toggle */}
            {selection.id && isAdmin && !isEditing && (
              <button
                onClick={() => setBuildingEditMode(true)}
                style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '5px 12px', borderRadius: '8px', border: '1px solid #E5E7EB', background: '#fff', cursor: 'pointer', fontSize: '13px', color: '#374151', fontWeight: 500, marginRight: '8px' }}
              >
                <Pencil size={13} color="#6B7280" /> Edit
              </button>
            )}
            {selection.id && isEditing && (
              <button
                onClick={() => { setBuildingEditMode(false); if (originalBuildingForm) setBuildingForm(originalBuildingForm); }}
                style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '5px 10px', borderRadius: '8px', border: '1px solid #E5E7EB', background: '#fff', cursor: 'pointer', fontSize: '13px', color: '#9CA3AF', marginRight: '8px' }}
              >
                <X size={13} /> Cancel edit
              </button>
            )}
            {/* Bell alert — only shown when there are unassigned matching estates */}
            {unassignedPreview.length > 0 && (
              <div style={{ position: 'relative' }}>
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer', padding: '5px 10px', borderRadius: '20px', backgroundColor: '#FEF3C7', border: '1px solid #FDE68A' }}
                  onClick={() => setShowMatchPopover(o => !o)}
                >
                  <Bell size={14} color="#D97706" />
                  <span style={{ fontSize: '12px', fontWeight: 600, color: '#D97706' }}>{unassignedPreview.length} to assign</span>
                </div>
                {showMatchPopover && (
                  <div style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, width: '320px', backgroundColor: '#fff', border: '1px solid #E5E7EB', borderRadius: '12px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', zIndex: 500, padding: '14px' }}>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: '#111827', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Bell size={13} color="#D97706" />
                      {unassignedPreview.length} unassigned estates match "{buildingForm.name}"
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap' as const, gap: '4px', marginBottom: '12px', maxHeight: '120px', overflowY: 'auto' }}>
                      {unassignedPreview.map(e => (
                        <span key={e.id} style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '10px', backgroundColor: '#D1FAE5', color: '#065F46', border: '1px solid #6EE7B7' }}>
                          {e.name}
                        </span>
                      ))}
                    </div>
                    {alreadyAssigned.length > 0 && (
                      <div style={{ fontSize: '11px', color: '#92400E', backgroundColor: '#FEF3C7', padding: '6px 8px', borderRadius: '6px', marginBottom: '10px' }}>
                        {alreadyAssigned.length} already assigned to another building
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <Button
                        size="mini"
                        kind="tertiary"
                        onClick={() => setShowMatchPopover(false)}
                        overrides={{ BaseButton: { style: { flex: 1 } } }}
                      >
                        Dismiss
                      </Button>
                      {selection.id && (
                        <Button
                          size="mini"
                          onClick={() => {
                            setShowMatchPopover(false);
                            openConfirm(
                              'Assign Estates',
                              `Assign ${unassignedPreview.length} estate(s) to building "${buildingForm.name}"? They will inherit the building's Hik-Connect access level.`,
                              async () => {
                                await bulkAssign.mutateAsync({ estateIds: unassignedPreview.map(e => e.id), buildingId: selection.id! });
                                queryClient.invalidateQueries({ queryKey: ['estates-flat-tree'] });
                              }
                            );
                          }}
                          isLoading={bulkAssign.isPending}
                          overrides={{ BaseButton: { style: { flex: 2, backgroundColor: '#059669', color: '#fff' } } }}
                        >
                          Assign all {unassignedPreview.length}
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </RightPanelTitle>
        </RightHeader>

        <RightScroll>
          {/* Building Details — view / edit */}
          <FormCard>
            {!isEditing ? (
              /* ── VIEW MODE ── */
              <div>
                <div style={{ fontSize: '18px', fontWeight: 700, color: '#111827', marginBottom: '12px' }}>
                  {selectedBuilding?.name}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <span style={{ fontSize: '12px', color: '#9CA3AF', width: 90, flexShrink: 0 }}>Group</span>
                    <span style={{ fontSize: '13px', color: '#374151', fontWeight: 500 }}>{bGroup?.name || '—'}</span>
                  </div>
                  {buildingForm.description && (
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <span style={{ fontSize: '12px', color: '#9CA3AF', width: 90, flexShrink: 0 }}>Description</span>
                      <span style={{ fontSize: '13px', color: '#374151' }}>{buildingForm.description}</span>
                    </div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '12px', color: '#9CA3AF', width: 90, flexShrink: 0 }}>Hik-Connect</span>
                    {buildingForm.hik_access_level_id
                      ? <HikBadge $level="building">{hikName(buildingForm.hik_access_level_id) || buildingForm.hik_access_level_id}</HikBadge>
                      : <span style={{ fontSize: '12px', color: '#D1D5DB' }}>Inherit from group</span>
                    }
                  </div>
                </div>
              </div>
            ) : (
              /* ── EDIT MODE ── */
              <>
                <FC label="Name *" overrides={{ Label: { style: { fontSize: '12px' } } }}>
                  <Inp value={buildingForm.name || ''} onChange={(e: any) => setBuildingForm((p: any) => ({ ...p, name: e.target.value }))} placeholder="e.g. Building Name" />
                </FC>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
                  <FC label="Estate Group *" overrides={{ Label: { style: { fontSize: '12px' } } }}>
                    <Sel options={groupOptions} value={selectedGroupForBuilding} placeholder="Select group"
                      onChange={(p: any) => setBuildingForm((prev: any) => ({ ...prev, estate_group_id: p.value[0]?.id ?? null }))}
                      overrides={SELECT_OVERRIDES} />
                  </FC>
                  <FC label="Description" overrides={{ Label: { style: { fontSize: '12px' } } }}>
                    <Inp value={buildingForm.description || ''} onChange={(e: any) => setBuildingForm((p: any) => ({ ...p, description: e.target.value }))} placeholder="Optional" />
                  </FC>
                </div>
                {showHik && renderHikSelector(
                  buildingForm.hik_access_level_id,
                  (v) => setBuildingForm((p: any) => ({ ...p, hik_access_level_id: v })),
                  'Hik-Connect Access Level',
                  true
                )}

                {/* Auto-assign flag — only when creating and there are unassigned matches */}
                {!selection.id && unassigned.length > 0 && (
                  <div
                    style={{ marginTop: '14px', padding: '10px 14px', borderRadius: '8px', backgroundColor: buildingAutoAssign ? 'color-mix(in srgb, var(--tenant-primary) 8%, white)' : '#F9FAFB', border: `1px solid ${buildingAutoAssign ? 'var(--tenant-primary)' : '#E5E7EB'}`, cursor: 'pointer', transition: 'all 0.15s', display: 'flex', alignItems: 'center', gap: '10px' }}
                    onClick={() => setBuildingAutoAssign(o => !o)}
                  >
                    {/* Custom checkbox */}
                    <div style={{ width: 16, height: 16, borderRadius: 4, border: `2px solid ${buildingAutoAssign ? 'var(--tenant-primary)' : '#D1D5DB'}`, backgroundColor: buildingAutoAssign ? 'var(--tenant-primary)' : '#fff', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.1s' }}>
                      {buildingAutoAssign && <svg width="9" height="7" viewBox="0 0 9 7" fill="none"><path d="M1 3.5L3.5 6L8 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '13px', fontWeight: 500, color: '#111827' }}>
                        Auto-assign {unassigned.length} matching estate{unassigned.length !== 1 ? 's' : ''}
                      </div>
                      <div style={{ fontSize: '11px', color: '#9CA3AF', marginTop: '1px' }}>
                        Estates matching "{buildingForm.name}" will be assigned when building is created
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </FormCard>

          {/* Current estates */}
          {selection.id && bEstates.length > 0 && (
            <FormCard>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                <Home size={14} color="#6B7280" />
                <span style={{ fontSize: '13px', fontWeight: 600, color: '#374151', flex: 1 }}>
                  Estates ({bEstates.length})
                </span>
                {bEstates.length > 5 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', backgroundColor: '#F3F4F6', borderRadius: '6px', padding: '4px 8px' }}>
                    <Search size={12} color="#9CA3AF" />
                    <input
                      value={buildingEstateSearch}
                      onChange={e => setBuildingEstateSearch(e.target.value)}
                      placeholder="Filter estates..."
                      style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: '12px', color: '#374151', width: '110px' }}
                    />
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', maxHeight: '260px', overflowY: 'auto' }}>
                {filteredBEstates.map(e => {
                  const hikLevel = e.hik_access_level_id;
                  return (
                    <div
                      key={e.id}
                      style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 10px', borderRadius: '6px', backgroundColor: '#FAFAFA', border: '1px solid #F3F4F6', cursor: 'pointer' }}
                      onClick={() => select('estate', e.id)}
                    >
                      <Home size={13} color="#9CA3AF" />
                      <div style={{ flex: 1, fontSize: '13px', fontWeight: 500 }}>{e.name}</div>
                      <span style={{ fontSize: '11px', color: '#9CA3AF' }}>{e.status}</span>
                      {estateUserCountMap[e.id!] > 0 && (
                        <span style={{ fontSize: '11px', color: '#fff', backgroundColor: '#6B7280', borderRadius: '10px', padding: '1px 7px', fontWeight: 600 }}>
                          {estateUserCountMap[e.id!]}
                        </span>
                      )}
                      {hikLevel
                        ? <HikBadge $level="estate">{hikName(hikLevel) || 'custom'}</HikBadge>
                        : <HikBadge $level="default">inherit</HikBadge>}
                      <ChevronRight size={13} color="#D1D5DB" />
                    </div>
                  );
                })}
                {filteredBEstates.length === 0 && (
                  <div style={{ padding: '12px', textAlign: 'center', color: '#9CA3AF', fontSize: '13px' }}>
                    No estates match "{buildingEstateSearch}"
                  </div>
                )}
              </div>
            </FormCard>
          )}

        </RightScroll>

        <StickyFooter>
          <FooterActions>
            {selection.id && isAdmin && isEditing && (
              <Button kind="tertiary" onClick={deleteSelected} startEnhancer={() => <Trash2 size={13} />}
                overrides={{ BaseButton: { style: { color: '#EF4444', paddingLeft: '10px', paddingRight: '10px' } } }}>
                Delete
              </Button>
            )}
          </FooterActions>
          <FooterActions>
            {isEditing && (
              <>
                <Button kind="tertiary" onClick={() => selection.id ? setBuildingEditMode(false) : setSelection({ type: null, id: null })}>Cancel</Button>
                <Button onClick={saveBuilding} isLoading={cBuilding || uBuilding} disabled={!buildingDirty} overrides={{ BaseButton: { style: { minWidth: '120px' } } }}>
                  {selection.id ? 'Save Changes' : 'Create Building'}
                </Button>
              </>
            )}
          </FooterActions>
        </StickyFooter>
      </>
    );
  };

  // ─── ESTATE PANEL ────────────────────────────────────────────────────
  const renderEstatePanel = () => {
    const groupOptions = (allEstateGroups || []).map(g => ({ id: g.id, label: g.name || `Group ${g.id}` }));
    // No "No building" sentinel option here: building_id already defaults to
    // null for "unassigned", so an explicit id:null option collided with the
    // "nothing selected" state and made the field impossible to clear (SAM-284).
    // An empty selection (placeholder shown) already means "no building".
    const buildingOptions = allBuildings.map(b => ({ id: b.id, label: b.name || `Building ${b.id}` }));
    const selectedGroupOpt = groupOptions.filter(o => o.id === estateForm.estate_group_id);
    const selectedBuildingOpt = buildingOptions.filter(o => o.id === estateForm.building_id);
    const typeOptions = withStoredOption(TYPE_OPTS, estateForm.estate_type_id, storedTypeLabel);
    const statusOptions = withStoredOption(STATUS_OPTS, estateForm.status, storedStatusLabel);
    const selectedTypeOpt = typeOptions.filter(o => o.id === estateForm.estate_type_id);
    const selectedStatusOpt = statusOptions.filter(o => o.id === estateForm.status);

    // Inheritance chain
    const parentBuilding = estateForm.building_id ? buildingById.get(estateForm.building_id) : null;
    const effectiveHik =
      estateForm.hik_access_level_id ||
      (parentBuilding as any)?.hik_access_level_id ||
      (estateGroup as any)?.hik_access_level_id ||
      null;

    return (
      <>
        <RightHeader>
          <Breadcrumb>
            {estateGroup && (
              <>
                <Crumb $clickable onClick={() => select('group', estateGroup.id!)}>{estateGroup.name}</Crumb>
                <span>/</span>
              </>
            )}
            {estateBuilding && (
              <>
                <Crumb $clickable onClick={() => select('building', estateBuilding.id!)}>{estateBuilding.name}</Crumb>
                <span>/</span>
              </>
            )}
            <Crumb $clickable={false}>{selectedEstate?.name || 'Estate'}</Crumb>
          </Breadcrumb>
          <RightPanelTitle>
            <Home size={20} color="#6B7280" />
            {selection.id ? (selectedEstate?.name || 'Estate') : 'New Estate'}
          </RightPanelTitle>
        </RightHeader>

        <RightScroll>
          {/* Read-only for the roles whose matrix gives Estates no edit (tenant and estate group Front Desk); the
              server refuses their estate writes anyway. */}
          <ReadOnlyFence section="estates">
          <FormCard>
            <CardTitle><Home size={14} color="#6B7280" />Estate Details</CardTitle>
            <FC label="Name">
              <Inp
                value={estateForm.name || ''}
                onChange={(e: any) => setEstateForm((p: any) => ({ ...p, name: e.target.value }))}
                placeholder="e.g. Unit A1"
                disabled={!canEditEstates}
              />
            </FC>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
              <FC label="Estate Group">
                <Sel
                  options={groupOptions}
                  value={selectedGroupOpt}
                  placeholder="Select group"
                  onChange={(p: any) => setEstateForm((prev: any) => ({ ...prev, estate_group_id: p.value[0]?.id ?? null }))}
                  overrides={SELECT_OVERRIDES}
                  disabled={!canEditEstates || isEstateGroupAdmin || isEstateGroupFrontDesk}
                />
              </FC>
              <FC label="Building">
                <Sel
                  options={buildingOptions}
                  value={selectedBuildingOpt}
                  placeholder="Select building"
                  onChange={(p: any) => setEstateForm((prev: any) => ({ ...prev, building_id: p.value[0]?.id ?? null }))}
                  overrides={SELECT_OVERRIDES}
                  disabled={!canEditEstates}
                />
              </FC>
              <FC label="Type">
                <Sel
                  options={typeOptions}
                  value={selectedTypeOpt}
                  placeholder={selectPlaceholder(canEditEstates, 'Select type')}
                  onChange={(p: any) => setEstateForm((prev: any) => ({ ...prev, estate_type_id: p.value[0]?.id ?? null }))}
                  overrides={SELECT_OVERRIDES}
                  disabled={!canEditEstates}
                />
              </FC>
              <FC label="Status">
                <Sel
                  options={statusOptions}
                  value={selectedStatusOpt}
                  placeholder={selectPlaceholder(canEditEstates, 'Select status')}
                  onChange={(p: any) => setEstateForm((prev: any) => ({ ...prev, status: p.value[0]?.id ?? '' }))}
                  overrides={SELECT_OVERRIDES}
                  disabled={!canEditEstates}
                />
              </FC>
            </div>
            <div style={{ marginTop: '8px' }}>
              <Checkbox
                checked={estateForm.show_in_app ?? true}
                onChange={(e: any) => setEstateForm((p: any) => ({ ...p, show_in_app: e.target.checked }))}
                labelPlacement={LABEL_PLACEMENT.right}
                disabled={!canEditEstates}
              >
                <span style={{ fontSize: '13px' }}>Show in App</span>
              </Checkbox>
            </div>
          </FormCard>

          {/* Hik inheritance badge */}
          {showHik && effectiveHik && !estateForm.hik_access_level_id && (
            <div style={{ padding: '10px 14px', backgroundColor: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: '8px', marginBottom: '16px', fontSize: '12px', color: '#065F46', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <DoorOpen size={14} />
              Inheriting Hik level from {(parentBuilding as any)?.hik_access_level_id ? `building "${parentBuilding?.name}"` : `group`}: <strong>{hikName(effectiveHik)}</strong>
            </div>
          )}

          {showHik && renderHikSelector(
            estateForm.hik_access_level_id,
            (v) => setEstateForm((p: any) => ({ ...p, hik_access_level_id: v })),
            'Hik-Connect Override (estate level)'
          )}
          </ReadOnlyFence>

          {selection.id && (
            <FormCard style={{ marginTop: '16px' }}>
              <CardTitle><Home size={14} color="#6B7280" />Associated Users</CardTitle>
              <MantineDataTable
                columns={estateUserColumns}
                data={estateUsers || []}
                loading={isLoadingEstateUsers}
                enablePagination={false}
                enableSorting={true}
                enableColumnFilters={false}
                enableGlobalFilter={false}
              />
            </FormCard>
          )}
        </RightScroll>

        <StickyFooter>
          <FooterActions>
            {selection.id && isAdmin && (
              <Button
                kind="tertiary"
                onClick={deleteSelected}
                startEnhancer={() => <Trash2 size={13} />}
                overrides={{ BaseButton: { style: { color: '#EF4444', paddingLeft: '10px', paddingRight: '10px' } } }}
              >
                Delete
              </Button>
            )}
          </FooterActions>
          <FooterActions>
            {/* SAM-1320 (U42): view-only roles have nothing to cancel */}
            {canEditEstates && <Button kind="tertiary" onClick={() => setSelection({ type: null, id: null })}>Cancel</Button>}
            {canEditEstates && (
              <Button onClick={saveEstate} isLoading={cEstate || uEstate} disabled={!estateDirty} overrides={{ BaseButton: { style: { minWidth: '120px' } } }}>
                {selection.id ? 'Save Changes' : 'Create Estate'}
              </Button>
            )}
          </FooterActions>
        </StickyFooter>
      </>
    );
  };

  // ═════════════════════════════════════════════════════════════════════
  return (
    <Shell>
      {/* ── LEFT: Tree ──────────────────────────────────────────── */}
      <LeftPanel>
        <LeftHeader>
          <LeftTitle>Estates</LeftTitle>
          <SearchBox>
            <Search size={14} color="#9CA3AF" />
            <SearchInput
              value={treeSearch}
              onChange={e => setTreeSearch(e.target.value)}
              placeholder="Search groups, buildings, units..."
            />
          </SearchBox>
        </LeftHeader>

        {(estatesLoading && allEstates.length === 0) ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
            <Spinner $size={28} />
          </div>
        ) : renderTree()}

        {isAdmin && (
          <LeftFooter>
            <div style={{ display: 'flex', gap: '6px' }}>
              <Button
                size="compact"
                kind="secondary"
                onClick={() => setSelection({ type: 'group', id: null })}
                startEnhancer={() => <Plus size={13} />}
                overrides={{ BaseButton: { style: { flex: 1, fontSize: '12px' } } }}
              >
                Group
              </Button>
              <Button
                size="compact"
                kind="secondary"
                onClick={() => {
                  // Pre-fill estate_group_id if currently viewing a group
                  const preGroupId = selection.type === 'group' ? selection.id : null;
                  setBuildingForm({ name: '', description: '', estate_group_id: preGroupId, hik_access_level_id: null });
                  setSelection({ type: 'building', id: null });
                }}
                startEnhancer={() => <Plus size={13} />}
                overrides={{ BaseButton: { style: { flex: 1, fontSize: '12px' } } }}
              >
                Building
              </Button>
              <Button
                size="compact"
                kind="secondary"
                onClick={() => setSelection({ type: 'estate', id: null })}
                startEnhancer={() => <Plus size={13} />}
                overrides={{ BaseButton: { style: { flex: 1, fontSize: '12px' } } }}
              >
                Estate
              </Button>
            </div>
          </LeftFooter>
        )}
      </LeftPanel>

      {/* ── RIGHT: Detail / Edit ─────────────────────────────────── */}
      <RightPanel>
        {renderRight()}
      </RightPanel>

      {/* ── Confirm modal ────────────────────────────────────────── */}
      {(() => {
        const M = Modal as any;
        const MH = ModalHeader as any;
        const MB = ModalBody as any;
        const MF = ModalFooter as any;
        const MBtn = ModalButton as any;
        return (
          <M
            isOpen={confirmModal.open}
            onClose={closeConfirm}
            role={ROLE.alertdialog}
            overrides={{
              Root: { style: { zIndex: 10002 } },
              Dialog: { style: { borderRadius: '12px', padding: '8px' } },
            }}
          >
            <MH>{confirmModal.title}</MH>
            <MB>
              <p style={{ fontSize: '14px', color: '#374151', margin: 0, lineHeight: 1.6 }}>
                {confirmModal.body}
              </p>
            </MB>
            <MF>
              <MBtn kind="tertiary" onClick={closeConfirm}>Cancel</MBtn>
              <MBtn
                onClick={() => { confirmModal.onConfirm(); closeConfirm(); }}
                overrides={confirmModal.danger ? {
                  BaseButton: { style: { backgroundColor: '#EF4444', ':hover': { backgroundColor: '#DC2626' } } }
                } : {}}
              >
                Confirm
              </MBtn>
            </MF>
          </M>
        );
      })()}
    </Shell>
  );
};
